import { z as zod } from 'zod';
import { toast } from 'sonner';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { LoadingButton } from '@mui/lab';
import { Card, Stack, Typography } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { Form, Field } from 'src/components/hook-form';
import { ButtonEdit } from 'src/components/button-edit';

import type { IProfileCommon } from './types';

// Generic profile schema that can be used for both user and organization profiles
export const ProfileSchema = zod.object({
  displayName: zod.string().min(2, { message: 'Name must be at least 2 characters.' }),
  tagline: zod
    .string()
    .optional()
    .refine((val) => !val || (val.length >= 2 && val.length <= 50), {
      message: 'Tagline must be between 2 and 50 characters.',
    }),
  bio: zod
    .string()
    .optional()
    .refine((val) => !val || (val.length >= 1 && val.length <= 200), {
      message: 'Bio must be between 1 and 200 characters.',
    }),
  image: zod.union([zod.instanceof(File), zod.string()]).optional(),
  webURL: zod
    .string()
    .optional()
    .refine((val) => !val || zod.string().url().safeParse(val).success, {
      message: 'Must be a valid URL.',
    })
    .refine((val) => !val || (val.length >= 2 && val.length <= 200), {
      message: 'Web URL must be between 2 and 200 characters.',
    }),
  thumbnailArea: zod
    .object({
      x1: zod.number(),
      y1: zod.number(),
      x2: zod.number(),
      y2: zod.number(),
    })
    .optional(),
});

export type ProfileSchemaType = zod.infer<typeof ProfileSchema>;

// Props for the ProfileEditor component
export interface ProfileEditorProps {
  profile: IProfileCommon;
  onUpdate: (data: Partial<ProfileSchemaType>) => Promise<any>;
  isUpdating?: boolean;
  disableNameEdit?: boolean;
  nameLabel?: string;
  taglineLabel?: string;
  webUrlLabel?: string;
  bioLabel?: string;
  inDialog?: boolean;
}

export function ProfileEditor({
  profile,
  onUpdate,
  isUpdating = false,
  disableNameEdit = false,
  nameLabel = 'Name',
  taglineLabel = 'Tagline',
  webUrlLabel = 'Web URL',
  bioLabel = 'Bio',
  inDialog = false,
}: ProfileEditorProps) {
  const edit = useBoolean();

  const defaultValues = useMemo(
    () => ({
      displayName: profile.displayName || '',
      tagline: profile.tagline || '',
      bio: profile.bio || '',
      webURL: profile.webURL || '',
      thumbnailArea: {
        x1: profile.image?.thumbnailX1 || 0,
        y1: profile.image?.thumbnailY1 || 0,
        x2: profile.image?.thumbnailX2 || 1,
        y2: profile.image?.thumbnailY2 || 1,
      },
    }),
    [profile]
  );

  const methods = useForm<ProfileSchemaType>({
    mode: 'all',
    resolver: zodResolver(ProfileSchema),
    defaultValues,
  });

  const { handleSubmit, reset } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await onUpdate({
        bio: data.bio,
        tagline: data.tagline,
        displayName: data.displayName,
        webURL: data.webURL,
      });
      toast.success('Profile updated successfully');
      edit.onFalse();
      reset();
    } catch (err: any) {
      console.error(err);
    }
  });

  const handleCancel = () => {
    edit.onFalse();
    reset();
  };

  const renderView = (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="flex-end">
        <ButtonEdit onClick={edit.onTrue} />
      </Stack>
      <Stack spacing={1} px={inDialog ? 2 : 0}>
        <Typography variant="h4">{profile?.displayName}</Typography>
        {profile?.tagline && <Typography variant="body1">{profile?.tagline}</Typography>}
        {profile?.bio && <Typography variant="body1">{profile?.bio}</Typography>}
        {profile?.webURL && <Link to={profile?.webURL}><Typography variant="body1">{profile?.webURL}</Typography></Link>}
      </Stack>
    </Stack>
  );

  const renderEdit = (
    <Form methods={methods} onSubmit={onSubmit}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="flex-end">
          <LoadingButton
            type="button"
            variant="outlined"
            sx={{ ml: 2 }}
            onClick={handleCancel}
            disabled={isUpdating}
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            type="submit"
            variant="contained"
            disabled={!methods.formState.isDirty}
            loading={isUpdating}
            color="primary"
            sx={{ ml: 2 }}
          >
            Save
          </LoadingButton>
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2">{nameLabel}</Typography>
          <Field.Text disabled={disableNameEdit} name="displayName" />
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2">{taglineLabel}</Typography>
          <Field.Text name="tagline" />
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2">{bioLabel}</Typography>
          <Field.Text multiline rows={4} name="bio" maxLength={200} />
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2">{webUrlLabel}</Typography>
          <Field.Text name="webURL" />
        </Stack>
      </Stack>
    </Form>
  );

  return (
    <Card
      style={inDialog ? { outline: '1px solid #E0E0E0' } : undefined}
      sx={{
        p: 2,
        boxShadow: inDialog ? 24 : undefined,
        bgcolor: 'background.paper',
        borderRadius: 2,
        flexGrow: 1,
      }}
    >
      {edit.value ? renderEdit : renderView}
    </Card>
  );
}
